// Wait for SwiperSlider to be defined before extending it
const initSwiperSliderOverlayHeader = () => {
  if (typeof customElements.get('swiper-slider') !== 'undefined') {
    // SwiperSlider is defined, we can extend it
    class SwiperSliderOverlayHeader extends SwiperSlider {
      constructor() {
        super();
        // État initial
        this.activeColorScheme = '';
        this.initialColorClass = '';
        this.originalLogoSrc = '';
        this.originalLogoSrcset = '';

        // Cache pour les éléments DOM et données des slides
        this.cache = {
          header: null,
          logoWrapper: null,
          logoImg: null,
          slideData: [],
        };

        // Contrôle des mises à jour
        this.isUpdating = false;
        this.lastUpdateTime = 0;
        this.updateQueue = [];
        this.throttleDelay = 100; // ms entre les mises à jour
      }

      connectedCallback() {
        super.connectedCallback();

        // Si overlay header est activé, configurer la fonctionnalité
        if (this.dataset.overlayHeader === 'true') {
          // Attendre que Swiper soit complètement initialisé
          if (this.swiper && this.swiper.initialized) {
            this.setupHeaderOverlay();
          } else {
            // Attendre l'initialisation complète de Swiper
            this.addEventListener('swiper:ready', () => {
              this.setupHeaderOverlay();
            });
          }
        }
      }

      // Initialiser le cache des éléments DOM
      initCache() {
        this.cache.header = document.querySelector('.header-wrapper');

        if (this.cache.header) {
          this.cache.logoWrapper = this.cache.header.querySelector('.header__heading-logo-wrapper');

          if (this.cache.logoWrapper) {
            this.cache.logoImg = this.cache.logoWrapper.querySelector('img');
          }
        }
      }

      // Précharger les données des slides
      preloadSlideData() {
        if (!this.swiper || !this.swiper.slides) return;

        this.cache.slideData = [];

        Array.from(this.swiper.slides).forEach((slide) => {
          const contentInner = slide.querySelector('.slideshow-slide__content-inner');
          // S'assurer que nous obtenons le schéma de couleur correctement
          const colorScheme = contentInner ? contentInner.classList[1] || '' : '';

          const logoTemplate = slide.querySelector('template[id^="Logo"]');
          let logoSrc = '';
          let logoSrcset = '';

          if (logoTemplate) {
            const templateContent = logoTemplate.content.cloneNode(true);
            const templateImg = templateContent.querySelector('img');

            if (templateImg) {
              logoSrc = templateImg.src;
              logoSrcset = templateImg.srcset || '';

              // Précharger l'image
              if (logoSrc) {
                const preloadImg = new Image();
                preloadImg.src = logoSrc;
              }
            }
          }

          this.cache.slideData.push({
            colorScheme,
            logoSrc,
            logoSrcset,
          });
        });

        // Log pour déboguer
        console.log('Données des slides préchargées:', this.cache.slideData);
      }

      // Configuration de l'overlay du header
      setupHeaderOverlay() {
        // Initialiser le cache
        this.initCache();

        if (!this.swiper || !this.cache.header) return;

        // Précharger les données des slides
        this.preloadSlideData();

        // Stocker l'état initial du header
        this.storeInitialHeaderState();

        // Appliquer l'état initial
        this.updateHeaderState(this.swiper.realIndex, false, true);

        // Mettre à jour le header et le swiper lors du changement de slide
        this.swiper.on('slideChange', () => {
          console.log('Slide changé vers:', this.swiper.realIndex);
          // Appliquer directement le changement pour le slide actif
          this.updateHeaderState(this.swiper.realIndex, false, true);
        });

        // Configurer les événements de survol du header
        this.setupHeaderHoverEvents();
      }

      // Mettre en file d'attente une mise à jour du header
      queueHeaderUpdate(slideIndex, isHovered) {
        // Ajouter la mise à jour à la file d'attente
        this.updateQueue.push({ slideIndex, isHovered });

        // Traiter la file d'attente si aucune mise à jour n'est en cours
        if (!this.isUpdating) {
          this.processUpdateQueue();
        }
      }

      // Traiter la file d'attente des mises à jour
      processUpdateQueue() {
        if (this.updateQueue.length === 0) {
          this.isUpdating = false;
          return;
        }

        this.isUpdating = true;

        // Prendre la dernière mise à jour de la file d'attente (ignorer les intermédiaires)
        const lastUpdate = this.updateQueue[this.updateQueue.length - 1];
        this.updateQueue = [];

        // Appliquer la mise à jour avec throttling
        const now = Date.now();
        const timeSinceLastUpdate = now - this.lastUpdateTime;

        if (timeSinceLastUpdate < this.throttleDelay) {
          // Si trop récent, attendre avant de mettre à jour
          setTimeout(() => {
            this.updateHeaderState(lastUpdate.slideIndex, lastUpdate.isHovered);
            this.processUpdateQueue();
          }, this.throttleDelay - timeSinceLastUpdate);
        } else {
          // Sinon, mettre à jour immédiatement
          this.updateHeaderState(lastUpdate.slideIndex, lastUpdate.isHovered);
          this.processUpdateQueue();
        }
      }

      // Mettre à jour l'état du header
      updateHeaderState(slideIndex, isHovered, skipThrottle = false) {
        if (
          !this.cache.header ||
          (!skipThrottle && this.isUpdating && Date.now() - this.lastUpdateTime < this.throttleDelay)
        ) {
          return;
        }

        this.lastUpdateTime = Date.now();

        // Utiliser requestAnimationFrame pour synchroniser avec le cycle de rendu
        const isFocused = this.cache.header.contains(document.activeElement);
        requestAnimationFrame(() => {
          if (isHovered || isFocused) {
            this.applyNormalHeaderState();
          } else {
            this.applyTransparentHeaderState(slideIndex);
          }
        });
      }

      // Stocker l'état initial du header
      storeInitialHeaderState() {
        if (!this.cache.header) return;

        // Stocker le schéma de couleur initial
        this.activeColorScheme = this.dataset.colorScheme;

        if (this.activeColorScheme != '') {
          // Stocker la classe initiale du header
          this.initialColorClass = this.cache.header.classList[1];
        }

        // Stocker les informations du logo original
        if (this.cache.logoImg) {
          this.originalLogoSrc = this.cache.logoImg.src;
          this.originalLogoSrcset = this.cache.logoImg.srcset || '';
        }
      }

      // Appliquer l'état normal du header (non transparent)
      applyNormalHeaderState() {
        if (!this.cache.header) return;

        // Supprimer la classe transparente
        this.cache.header.classList.remove('sticky-header--transparent');

        // Restaurer le schéma de couleur original pour le header
        this.cache.header.classList.remove(this.activeColorScheme);
        this.cache.header.classList.add(this.initialColorClass);

        // Restaurer le logo original
        this.updateLogo(this.originalLogoSrc, this.originalLogoSrcset);
      }

      // Appliquer l'état transparent du header
      applyTransparentHeaderState(slideIndex) {
        if (!this.cache.header || !this.swiper || slideIndex === undefined) return;

        // Vérifier que l'index est valide
        if (slideIndex < 0 || slideIndex >= this.cache.slideData.length) {
          console.warn('Index de slide invalide:', slideIndex, 'Max:', this.cache.slideData.length - 1);
          return;
        }

        // Récupérer les données du slide
        const slideData = this.cache.slideData[slideIndex];
        if (!slideData) {
          console.warn("Données du slide non trouvées pour l'index:", slideIndex);
          return;
        }

        console.log('Applique le schéma de couleur:', slideData.colorScheme, 'pour le slide:', slideIndex);

        // Ajouter la classe transparente
        this.cache.header.classList.add('sticky-header--transparent');

        // Si aucun schéma de couleur n'est trouvé dans le cache, essayer de le récupérer directement
        let colorScheme = slideData.colorScheme;
        if (!colorScheme && this.swiper.slides[slideIndex]) {
          const contentInner = this.swiper.slides[slideIndex].querySelector('.slideshow-slide__content-inner');
          if (contentInner) {
            colorScheme = contentInner.classList[1] || '';
            console.log('Schéma de couleur récupéré directement:', colorScheme);
          }
        }

        if (colorScheme) {
          // Mettre à jour le schéma de couleur
          this.cache.header.classList.remove(this.initialColorClass);

          // Supprimer l'ancien schéma de couleur s'il existe
          if (this.activeColorScheme) {
            this.cache.header.classList.remove(this.activeColorScheme);
            this.classList.remove(this.activeColorScheme);
          }

          // Ajouter le nouveau schéma de couleur
          this.cache.header.classList.add(colorScheme);
          this.classList.add(colorScheme);

          // Stocker le nouveau schéma de couleur actif
          this.activeColorScheme = colorScheme;
        } else {
          console.warn('Aucun schéma de couleur trouvé pour le slide:', slideIndex);
        }

        // Mettre à jour le logo
        this.updateLogo(slideData.logoSrc, slideData.logoSrcset);
      }

      // Mettre à jour le logo
      updateLogo(src, srcset) {
        if (!this.cache.logoImg || !src) return;

        this.cache.logoImg.src = src;

        if (srcset) {
          this.cache.logoImg.srcset = srcset;
        } else {
          this.cache.logoImg.removeAttribute('srcset');
        }
      }

      // Configurer les événements de survol du header
      setupHeaderHoverEvents() {
        if (!this.cache.header) return;

        // Ajouter les écouteurs d'événements pour la souris
        this.cache.header.addEventListener('mouseenter', () => {
          this.queueHeaderUpdate(this.swiper.realIndex, true);
        });

        this.cache.header.addEventListener('mouseleave', () => {
          // Vérifier la position de défilement
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const headerHeight = this.cache.header.offsetHeight;
          const isScrolledPastHeader = scrollTop > headerHeight;

          // Revenir à l'état transparent uniquement si nous n'avons pas défilé au-delà du header
          if (!isScrolledPastHeader) {
            this.queueHeaderUpdate(this.swiper.realIndex, false);
          }
        });

        // Ajouter un écouteur d'événement pour le défilement
        window.addEventListener(
          'scroll',
          () => {
            // Vérifier la position de défilement
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const headerHeight = this.cache.header.offsetHeight;
            const isScrolledPastHeader = scrollTop > headerHeight;

            if (isScrolledPastHeader) {
              // Si défilé au-delà du header, appliquer l'état normal
              this.queueHeaderUpdate(this.swiper.realIndex, true);
            } else {
              // Si pas défilé au-delà du header, vérifier si la souris est sur le header
              const headerRect = this.cache.header.getBoundingClientRect();
              const mouseX = window.event ? window.event.clientX : 0;
              const mouseY = window.event ? window.event.clientY : 0;

              const isMouseOverHeader =
                mouseX >= headerRect.left &&
                mouseX <= headerRect.right &&
                mouseY >= headerRect.top &&
                mouseY <= headerRect.bottom;

              // Appliquer l'état transparent uniquement si la souris n'est pas sur le header
              if (!isMouseOverHeader) {
                this.queueHeaderUpdate(this.swiper.realIndex, false);
              }
            }
          },
          { passive: true },
        );
      }
    }

    // Enregistrer l'élément personnalisé
    if (!customElements.get('swiper-slider-overlay-header')) {
      customElements.define('swiper-slider-overlay-header', SwiperSliderOverlayHeader);
    }
  } else {
    // SwiperSlider n'est pas encore défini, attendre et réessayer
    setTimeout(initSwiperSliderOverlayHeader, 100);
  }
};

// Démarrer le processus d'initialisation
document.addEventListener('DOMContentLoaded', initSwiperSliderOverlayHeader);
// Essayer également d'initialiser immédiatement au cas où le DOM est déjà chargé
initSwiperSliderOverlayHeader();
